/**
 * หน้า "ออร์เดอร์ของฉัน" — LIFF → ID token → memberApi
 * - cache ใน sessionStorage อายุสั้นตามบัญชี LINE แล้วรีเฟรชเบื้องหลัง
 * - 401 ครั้งแรก: ใน LINE = reload หน้า (ได้ token ใหม่) · นอก LINE = logout + login · ครั้งที่สอง = แจ้งให้เปิดจาก LINE
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { LIFF_ID_BOT1, LIFF_ID_BOT2 } from '../config/env'
import { fetchCustomerOrders, fetchMyOrders, type MemberProfile } from '../lib/api/memberApi'
import { clearLiffLogin, hasFreshLiffLogin, markLiffLogin } from '../lib/liffSession'
import { resolveBotIdFromHref } from '../lib/orderUtils'
import { readUrlAs, type MemberOrdersResponse } from './myOrdersLogic'
import { isMemberProfileReady } from './memberEligibility'
import { clearMemberLogin, markMemberLogin, clearMemberSession, isMemberCacheFresh, memberCacheKeyForUser, readMemberCache, readMemberSession, saveMemberSession, tokenSession, writeMemberCache } from './memberSession'

export type MyOrdersState =
  | { phase: 'loading' }
  | { phase: 'ready'; data: MemberOrdersResponse }
  | { phase: 'auth'; message: string }
  | { phase: 'error'; message: string }

type LiffSdk = (typeof import('@line/liff'))['default']

const CACHE_KEY = 'oc_my_orders_v2'
const RELOGIN_KEY = 'oc_member_relogin'
const MSG_AUTH = 'กรุณาเปิดจาก LINE อีกครั้ง'
/** แคชอายุไม่ถึงนี้ = ไม่ยิง memberApi ตอนเปิดหน้า (ลด Firestore reads จาก listMyOrders) */
const MEMBER_CACHE_FRESH_MS = 60_000
/** กลับมาที่แท็บ/โฟกัส → รีเฟรชได้ไม่ถี่กว่านี้ (เดิม 30 วิ) */
const RESUME_REFRESH_MIN_MS = 2 * 60_000
/** เปิดถุงทอง → ดึงข้อมูลสด (ข้ามแคช) ได้ไม่ถี่กว่านี้ — กิจกรรม/ประกาศที่แอดมินเพิ่งบันทึกขึ้นทันทีโดยไม่ยิงซ้ำตอนกดรัว */
const ACTIVITY_REFRESH_MIN_MS = 30_000

/**
 * แคชผูกกับ LINE user ID ที่อยู่ใน ID token จึงใช้ได้ข้ามการ redirect หลัง login
 * โดยไม่เสี่ยงนำข้อมูลของบัญชี LINE คนก่อนมาแสดง
 */
async function memberCacheKey(idToken: string, botId: string, asOrderId: string | null, liff: LiffSdk | null): Promise<string | null> {
  try {
    const lineUserId = liff?.getDecodedIDToken()?.sub || tokenSession(idToken, botId)?.userId
    if (lineUserId) return memberCacheKeyForUser(lineUserId, botId, asOrderId)

    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(idToken))
    const tokenHash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
    return `${CACHE_KEY}:${botId}:${asOrderId || 'me'}:${tokenHash}`
  } catch {
    return null
  }
}

const liffInitializations = new Map<string, Promise<LiffSdk>>()
function initLiff(botId: string): Promise<LiffSdk> {
  const existing = liffInitializations.get(botId)
  if (existing) return existing
  const pending = import('@line/liff').then(async ({ default: liff }) => {
    await liff.init({ liffId: botId === '2' ? LIFF_ID_BOT2 : LIFF_ID_BOT1 })
    return liff
  }).catch((error) => {
    liffInitializations.delete(botId)
    throw error
  })
  liffInitializations.set(botId, pending)
  return pending
}

function memberLoginRedirectUrl(botId: string, asOrderId: string | null): string {
  const url = new URL('/', window.location.origin)
  url.searchParams.set('view', 'orders')
  if (botId === '2') url.searchParams.set('botId', '2')
  if (asOrderId) url.searchParams.set('as', asOrderId)
  return url.toString()
}

export function useMyOrders() {
  const [botId] = useState(() => resolveBotIdFromHref() || '1')
  /** โหมดแอดมินดูมุมมองลูกค้า (?as=ORDERID) — null = ออร์เดอร์ของตัวเอง */
  const [asOrderId] = useState(() => readUrlAs(window.location.search))
  const [session] = useState(() => {
    if (asOrderId) return null
    const saved = readMemberSession(botId, window.location.search)
    const harness = window.__OC_MEMBER_HARNESS__
    return harness && harness.idToken !== saved?.idToken ? null : saved
  })
  const [state, setState] = useState<MyOrdersState>(() => {
    const cached = session ? readMemberCache(memberCacheKeyForUser(session.userId, botId, null)) : null
    return cached ? { phase: 'ready', data: cached } : { phase: 'loading' }
  })
  const requestRef = useRef(0)
  const logoutRef = useRef(false)
  const liffRef = useRef<LiffSdk | null>(null)
  const cacheKeyRef = useRef<string | null>(null)

  const load = useCallback(
    async (force = false, skipCache = false) => {
      // ระหว่างรีเฟรชอย่าล้างหน้าที่แสดงข้อมูลแล้ว เพื่อลดการกระพริบ
      const requestId = ++requestRef.current
      const publish = (next: MyOrdersState) => { if (requestId === requestRef.current) setState(next) }
      setState((current) => current.phase === 'ready' ? current : { phase: 'loading' })
      const harness = window.__OC_MEMBER_HARNESS__

      let idToken = ''
      try {
        if (harness) {
          idToken = harness.idToken
        } else if (session && tokenSession(session.idToken, botId) && !force) {
          idToken = session.idToken
        } else {
          const liff = liffRef.current || (liffRef.current = await initLiff(botId))
          if (!liff.isLoggedIn()) {
            markLiffLogin(botId)
            liff.login({ redirectUri: memberLoginRedirectUrl(botId, asOrderId) })
            return
          }
          if (!liff.isInClient() && !hasFreshLiffLogin(botId)) {
            clearLiffLogin(botId)
            markLiffLogin(botId)
            liff.logout()
            liff.login({ redirectUri: memberLoginRedirectUrl(botId, asOrderId) })
            return
          }
          idToken = liff.getIDToken() || ''
          if (idToken) markLiffLogin(botId)
        }
      } catch {
        publish({ phase: 'error', message: 'เปิด LINE ไม่สำเร็จ กรุณาลองใหม่' })
        return
      }
      if (!idToken) {
        publish({ phase: 'auth', message: MSG_AUTH })
        return
      }
      const cacheKey = session && idToken === session.idToken
        ? memberCacheKeyForUser(session.userId, botId, asOrderId)
        : await memberCacheKey(idToken, botId, asOrderId, liffRef.current)
      if (requestId !== requestRef.current) return
      cacheKeyRef.current = cacheKey
      let cachedData: MemberOrdersResponse | null = null
      if (!force && !skipCache && cacheKey) {
        cachedData = readMemberCache(cacheKey)
        if (cachedData) {
          // แสดงข้อมูลรอบล่าสุดก่อน แล้วขอข้อมูลจริงต่อโดยไม่บล็อก UI
          publish({ phase: 'ready', data: cachedData })
          if (isMemberCacheFresh(cacheKey, MEMBER_CACHE_FRESH_MS)) return
        }
      }

      let res: Awaited<ReturnType<typeof fetchMyOrders>>
      try {
        res = asOrderId ? await fetchCustomerOrders(idToken, asOrderId) : await fetchMyOrders(idToken, botId)
      } catch {
        if (!cachedData) publish({ phase: 'error', message: 'เชื่อมต่อไม่สำเร็จ กรุณาลองใหม่' })
        return
      }

      if (requestId !== requestRef.current) return
      if (res.status === 401) {
        publish({ phase: 'auth', message: res.data.message || MSG_AUTH })
        clearMemberSession(botId)
        if (cacheKey) { try { localStorage.removeItem(cacheKey); sessionStorage.removeItem(cacheKey) } catch { /* private mode */ } }
        let tried = false
        try {
          tried = sessionStorage.getItem(RELOGIN_KEY) === '1'
          sessionStorage.setItem(RELOGIN_KEY, '1')
        } catch {
          tried = true
        }
        const liff = liffRef.current || (!harness ? await initLiff(botId).catch(() => null) : null)
        if (!tried && liff && !harness) {
          if (liff.isInClient()) {
            window.location.reload()
          } else {
            liff.logout()
            liff.login({ redirectUri: memberLoginRedirectUrl(botId, asOrderId) })
          }
          return
        }
        publish({ phase: 'auth', message: res.data.message || MSG_AUTH })
        return
      }
      if (res.status === 403 || res.status === 404 || res.status === 422) {
        publish({ phase: 'auth', message: res.data.message || 'ไม่มีสิทธิ์ดูรายการนี้' })
        return
      }
      if (!res.data.ok) {
        if (!cachedData) publish({ phase: 'error', message: res.data.message || 'ดึงรายการไม่สำเร็จ กรุณาลองใหม่' })
        return
      }
      try {
        sessionStorage.removeItem(RELOGIN_KEY)
      } catch {
        /* ignore */
      }
      if (!asOrderId) {
        saveMemberSession(idToken, botId)
        markMemberLogin(botId)
      }
      if (cacheKey) writeMemberCache(cacheKey, res.data)
      publish({ phase: 'ready', data: res.data })
    },
    [botId, asOrderId, session],
  )

  useEffect(() => {
    void load()
    return () => { requestRef.current += 1 }
  }, [load])

  const reload = useCallback(() => {
    void load(true)
  }, [load])

  // ใช้ token เดิม (ไม่ต้อง init LIFF ใหม่) · ระหว่างรอยังแสดงข้อมูลเดิม
  const lastActivityRefreshRef = useRef(0)
  const refreshActivities = useCallback(() => {
    const now = Date.now()
    if (logoutRef.current || now - lastActivityRefreshRef.current < ACTIVITY_REFRESH_MIN_MS) return
    lastActivityRefreshRef.current = now
    void load(false, true)
  }, [load])

  useEffect(() => {
    let lastRefresh = Date.now()
    const refresh = () => {
      if (document.visibilityState !== 'visible' || logoutRef.current || Date.now() - lastRefresh < RESUME_REFRESH_MIN_MS) return
      lastRefresh = Date.now()
      void load(true)
    }
    document.addEventListener('visibilitychange', refresh)
    window.addEventListener('focus', refresh)
    window.addEventListener('online', refresh)
    return () => {
      document.removeEventListener('visibilitychange', refresh)
      window.removeEventListener('focus', refresh)
      window.removeEventListener('online', refresh)
    }
  }, [load])

  const updateMemberProfile = useCallback((profile: MemberProfile) => {
    setState((current) => {
      if (current.phase !== 'ready') return current
      const data = { ...current.data, memberProfile: profile, benefitsEligible: isMemberProfileReady(profile) }
      if (cacheKeyRef.current) writeMemberCache(cacheKeyRef.current, data)
      return { phase: 'ready', data }
    })
  }, [])

  const logout = useCallback(async () => {
    if (logoutRef.current) return
    logoutRef.current = true
    requestRef.current += 1
    setState({ phase: 'auth', message: 'กำลังออกจากระบบ' })
    clearMemberLogin()
    try {
      if (!window.__OC_MEMBER_HARNESS__) {
        await Promise.race([
          (liffRef.current ? Promise.resolve(liffRef.current) : initLiff(botId)).then((liff) => liff.logout()),
          new Promise<void>((resolve) => window.setTimeout(resolve, 2500)),
        ])
      }
    } catch { /* Local session has already been cleared; the next login rechecks LINE. */ }
    window.location.replace(botId === '2' ? '/?botId=2' : '/')
  }, [botId])

  const close = useCallback(() => {
    if (liffRef.current?.isInClient()) liffRef.current.closeWindow()
    else if (/Line\//i.test(navigator.userAgent)) {
      void initLiff(botId).then((liff) => liff.isInClient() ? liff.closeWindow() : window.history.back()).catch(() => window.history.back())
    } else window.history.back()
  }, [botId])

  return { state, botId, asOrderId, reload, refreshActivities, close, logout, updateMemberProfile }
}
